import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import { AnimatedSection, AnimatedText } from '../components/AnimatedSection';
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Lütfen zorunlu alanları doldurun.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      toast.success('Mesajınız başarıyla iletildi!');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const whatsappUrl = `https://wa.me/905314037309?text=${encodeURIComponent('Merhaba, etkinlik hakkında bilgi almak istiyorum.')}`;

  return (
    <div style={{ backgroundColor: '#070708' }}>
      {/* Hero */}
      <section
        className="relative flex items-end overflow-hidden"
        style={{ minHeight: '45vh', paddingBottom: '4rem', paddingTop: '8rem', backgroundColor: '#0B0B0D' }}
      >
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 800px 400px at 20% 60%, rgba(201,168,76,0.05), transparent 60%)' }}
        />
        <div className="relative z-10 container-luxury">
          <motion.span
            className="overline mb-5 block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
          >
            İletişim
          </motion.span>
          <motion.h1
            className="display-xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.85, ease: [0.22,1,0.36,1] }}
          >
            Projeni Konuşalım.
          </motion.h1>
        </div>
      </section>

      {/* Contact layout */}
      <section className="section-py" style={{ paddingTop: '4rem' }}>
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Form - 3 cols */}
            <AnimatedSection className="lg:col-span-3">
              {success ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center text-center py-16"
                  style={{ border: '1px solid rgba(201,168,76,0.12)', minHeight: '480px' }}
                >
                  <CheckCircle size={48} className="text-[#C9A84C] mb-6" />
                  <h3 className="font-display text-3xl text-[#F5F0E8] mb-4">Mesajınız İletildi</h3>
                  <p className="body-text max-w-sm mb-8">Teşekkür ederiz. En kısa sürede sizinle iletişime geçeceğiz.</p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="btn-outline-gold text-xs tracking-[0.12em] uppercase"
                  >
                    Yeni Mesaj Gönder
                  </button>
                </motion.div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <h3 className="font-display text-2xl text-[#F5F0E8] mb-2">Mesaj Gönder</h3>
                  <p className="body-text mb-4">Detayları paylaşın — 24 saat içinde dönüş yapalım.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.55)]">Ad Soyad *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] bg-transparent outline-none transition-colors focus:border-[#C9A84C]"
                        style={{ border: '1px solid rgba(201,168,76,0.2)', backgroundColor: '#0E0F12' }}
                        placeholder="İsminiz"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.55)]">Telefon</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] bg-transparent outline-none transition-colors focus:border-[#C9A84C]"
                        style={{ border: '1px solid rgba(201,168,76,0.2)', backgroundColor: '#0E0F12' }}
                        placeholder="+90 5xx xxx xx xx"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.55)]">E-posta *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] bg-transparent outline-none transition-colors focus:border-[#C9A84C]"
                      style={{ border: '1px solid rgba(201,168,76,0.2)', backgroundColor: '#0E0F12' }}
                      placeholder="eposta@adresiniz.com"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.55)]">Konu</label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] bg-transparent outline-none transition-colors focus:border-[#C9A84C]"
                      style={{ border: '1px solid rgba(201,168,76,0.2)', backgroundColor: '#0E0F12' }}
                      placeholder="Nasıl yardımcı olabiliriz?"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-xs tracking-[0.1em] uppercase text-[rgba(245,240,232,0.55)]">Mesajınız *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 font-body text-sm text-[#F5F0E8] bg-transparent outline-none resize-none transition-colors focus:border-[#C9A84C]"
                      style={{ border: '1px solid rgba(201,168,76,0.2)', backgroundColor: '#0E0F12' }}
                      placeholder="Etkinliğiniz hakkında bize bilgi verin..."
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    disabled={loading}
                    className="btn-gold flex items-center justify-center gap-2 text-xs tracking-[0.12em] uppercase mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span>Gönderiliyor...</span>
                    ) : (
                      <><Send size={14} />Gönder</>
                    )}
                  </button>
                </form>
              )}
            </AnimatedSection>

            {/* Info - 2 cols */}
            <AnimatedSection delay={0.2} className="lg:col-span-2 flex flex-col gap-8">
              <div>
                <span className="overline mb-5 block">Bilgilerimiz</span>
                <div className="flex flex-col gap-6">
                  <a
                    href="tel:+905314037309"
                    className="flex items-start gap-4 group"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)' }}
                    >
                      <Phone size={15} className="text-[#C9A84C]" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-[rgba(245,240,232,0.45)] tracking-[0.1em] uppercase mb-1">Telefon</p>
                      <p className="font-body text-sm text-[#F5F0E8] group-hover:text-[#C9A84C] transition-colors">+90 531 403 73 09</p>
                    </div>
                  </a>

                  <a
                    href="mailto:info@imerevents.com"
                    className="flex items-start gap-4 group"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)' }}
                    >
                      <Mail size={15} className="text-[#C9A84C]" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-[rgba(245,240,232,0.45)] tracking-[0.1em] uppercase mb-1">E-posta</p>
                      <p className="font-body text-sm text-[#F5F0E8] group-hover:text-[#C9A84C] transition-colors">info@imerevents.com</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)' }}
                    >
                      <MapPin size={15} className="text-[#C9A84C]" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-[rgba(245,240,232,0.45)] tracking-[0.1em] uppercase mb-1">Konum</p>
                      <p className="font-body text-sm text-[#F5F0E8]">Ankara, Türkiye</p>
                      <p className="font-body text-xs text-[rgba(245,240,232,0.5)] mt-1">Türkiye genelinde hizmet</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="flex items-center gap-3 p-5 transition-all duration-200 group"
                style={{
                  backgroundColor: 'rgba(37,211,102,0.08)',
                  border: '1px solid rgba(37,211,102,0.25)',
                }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#25D366' }}
                >
                  <MessageCircle size={18} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-body text-sm font-medium text-[#F5F0E8] mb-0.5">WhatsApp'tan Yaz</p>
                  <p className="font-body text-xs text-[rgba(245,240,232,0.5)]">Hızlı yanıt için mesaj gönderin</p>
                </div>
                <MessageCircle size={14} className="text-[rgba(37,211,102,0.5)] group-hover:text-[#25D366] transition-colors" />
              </a>

              {/* Map placeholder */}
              <div
                className="relative overflow-hidden"
                style={{ height: '200px', backgroundColor: '#0E0F12', border: '1px solid rgba(201,168,76,0.08)' }}
              >
                <div className="absolute inset-0 flex items-center justify-center flex-col gap-2">
                  <MapPin size={28} className="text-[rgba(201,168,76,0.4)]" />
                  <span className="font-body text-xs text-[rgba(245,240,232,0.3)] tracking-[0.12em] uppercase">Ankara, Türkiye</span>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
